import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Images,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Folder as FolderIcon,
  X,
} from 'lucide-react';
import type { AssetFolder } from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useDismissable } from '../../lib/useDismissable';
import { Input } from '../../components/ui/Field';
import { useDialogs } from '../../components/ui/DialogProvider';

/** Which slice of the library the grid is showing. `folderId: null` = the "All" view. */
export type MediaView = { kind: 'folder'; folderId: string | null } | { kind: 'trash' };

const rowBase =
  'focus-ring flex flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm min-w-0';

/**
 * Folder rail. "All" (no folder filter) sits on top, each folder shows its asset
 * count with a rename/delete menu, a "New folder" inline form, and a pinned
 * "Trash" entry that flips the grid to the soft-deleted view. All entries are
 * mutually exclusive.
 */
export function MediaFolderSidebar({
  folders,
  view,
  onSelect,
  tenantKey,
}: {
  folders: AssetFolder[];
  view: MediaView;
  onSelect: (view: MediaView) => void;
  tenantKey: string;
}) {
  const queryClient = useQueryClient();
  const { confirm, alert, prompt } = useDialogs();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  const invalidateFolders = () =>
    void queryClient.invalidateQueries({ queryKey: ['asset-folders', tenantKey] });
  const invalidateMedia = () =>
    void queryClient.invalidateQueries({ queryKey: ['media', tenantKey] });

  const create = useMutation({
    mutationFn: () => api.createAssetFolder({ name: name.trim() }),
    onSuccess: () => {
      invalidateFolders();
      setName('');
      setAdding(false);
    },
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not create the folder.'),
      }),
  });

  const rename = useMutation({
    mutationFn: (vars: { id: string; name: string }) =>
      api.updateAssetFolder(vars.id, { name: vars.name }),
    onSuccess: invalidateFolders,
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not rename the folder.'),
      }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAssetFolder(id),
    onSuccess: (_res, id) => {
      invalidateFolders();
      invalidateMedia();
      // If the active folder was deleted, fall back to the All view.
      if (view.kind === 'folder' && view.folderId === id) {
        onSelect({ kind: 'folder', folderId: null });
      }
    },
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: actionErrorText(err, 'Could not delete the folder.'),
      }),
  });

  const allActive = view.kind === 'folder' && view.folderId === null;
  const trashActive = view.kind === 'trash';

  return (
    <nav aria-label="Media folders" className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => onSelect({ kind: 'folder', folderId: null })}
        aria-current={allActive ? 'true' : undefined}
        className={cn(
          rowBase,
          'font-medium',
          allActive ? 'bg-tint text-tint-fg' : 'text-ink-strong hover:bg-chip',
        )}
      >
        <Images className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
        <span className="truncate">All media</span>
      </button>

      {folders.map((folder) => (
        <FolderRow
          key={folder.id}
          folder={folder}
          active={view.kind === 'folder' && view.folderId === folder.id}
          onSelect={() => onSelect({ kind: 'folder', folderId: folder.id })}
          onRename={async () => {
            const next = await prompt({
              title: 'Rename folder',
              defaultValue: folder.name,
              confirmLabel: 'Rename',
            });
            if (next && next !== folder.name) rename.mutate({ id: folder.id, name: next });
          }}
          onDelete={async () => {
            if (
              await confirm({
                title: 'Delete folder',
                message: `Delete "${folder.name}"? Its media isn't deleted — it moves back to All.`,
                confirmLabel: 'Delete',
                danger: true,
              })
            ) {
              remove.mutate(folder.id);
            }
          }}
        />
      ))}

      {adding ? (
        <form
          className="flex items-center gap-1 px-0.5 py-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Folder name"
            aria-label="New folder name"
            autoFocus
            className="h-8 py-1 text-sm"
          />
          <button
            type="submit"
            aria-label="Create folder"
            disabled={!name.trim() || create.isPending}
            className="focus-ring rounded-md p-1.5 text-action hover:bg-tint hover:text-tint-fg disabled:opacity-40"
          >
            <Check className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Cancel"
            onClick={() => {
              setAdding(false);
              setName('');
            }}
            className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="focus-ring flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-ink-muted hover:bg-chip hover:text-ink"
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          New folder
        </button>
      )}

      <div className="mt-2 border-t border-line-soft pt-2">
        <button
          type="button"
          onClick={() => onSelect({ kind: 'trash' })}
          aria-current={trashActive ? 'true' : undefined}
          className={cn(
            rowBase,
            'font-medium',
            trashActive ? 'bg-tint text-tint-fg' : 'text-ink-strong hover:bg-chip',
          )}
        >
          <Trash2 className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
          <span className="truncate">Trash</span>
        </button>
      </div>
    </nav>
  );
}

function FolderRow({
  folder,
  active,
  onSelect,
  onRename,
  onDelete,
}: {
  folder: AssetFolder;
  active: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismissable(open, ref, () => setOpen(false));

  const itemCls =
    'focus-ring flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink-strong hover:bg-hover';

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div className="group flex items-center gap-0.5">
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          rowBase,
          active ? 'bg-tint text-tint-fg' : 'text-ink-strong hover:bg-chip',
        )}
      >
        <FolderIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
        <span className="flex-1 truncate">{folder.name}</span>
        <span className="shrink-0 text-xs text-ink-faint">{folder.assetCount ?? 0}</span>
      </button>

      <div ref={ref} className="relative shrink-0">
        <button
          type="button"
          aria-label={`Actions for ${folder.name}`}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border border-line bg-card py-1 shadow-panel"
          >
            <button type="button" role="menuitem" className={itemCls} onClick={() => run(onRename)}>
              <Pencil className="h-4 w-4 text-ink-faint" aria-hidden />
              Rename
            </button>
            <button
              type="button"
              role="menuitem"
              className="focus-ring flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
              onClick={() => run(onDelete)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
